import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Edit3 } from 'lucide-react';
import useResponsive from '../../hooks/useResponsive';
import useAdmin from '../../hooks/useAdmin';

/**
 * VINTAGE HEADER COMPONENT - FIXED NAVIGATION ISSUES
 * 
 * Design Philosophy: Classic typewriter aesthetic with proper navigation
 * - Fixed header overlap with proper z-index and backdrop
 * - Smooth scroll to top when clicking navigation
 * - Compact design to minimize space usage
 * - Better admin access integration
 */

const VintageHeader = ({ profileData, categories, onAdminClick }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const location = useLocation();
  const { isMobile } = useResponsive();
  const { isAdmin } = useAdmin();

  const isActive = (path) => location.pathname === path;

  const closeMenus = () => {
    setIsMenuOpen(false);
  };

  // Smooth scroll to top when navigating
  const handleNavClick = (e, path) => {
    if (location.pathname === path) {
      e.preventDefault();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    closeMenus();
  };

  return (
    <header className="vintage-header-fixed sticky top-0 z-50">
      <div className="max-w-6xl mx-auto px-4 py-4">
        
        {/* Header Layout */}
        <div className="flex items-center justify-between">
          
          {/* Site Title - Compact */}
          <Link 
            to="/" 
            onClick={(e) => handleNavClick(e, '/')}
            className="flex items-center space-x-3 group"
          >
            <div>
              <h1 className="vintage-heading-1 text-xl md:text-2xl text-vintage-ink group-hover:text-vintage-accent transition-colors">
                {profileData?.name || 'Jaswanth Vasudeva'}
              </h1>
              <p className="vintage-caption text-vintage-faded text-xs tracking-wider -mt-1">
                {profileData?.title || 'VC/PE Fellow & Strategic Leader'}
              </p>
            </div>
          </Link>

          {/* Navigation - Compact */}
          {!isMobile ? (
            <nav className="vintage-nav">
              <div className="flex items-center space-x-6">
                <Link 
                  to="/about" 
                  onClick={(e) => handleNavClick(e, '/about')}
                  className={`vintage-link px-3 py-2 text-sm transition-all rounded-lg hover:bg-vintage-paper ${
                    isActive('/about') ? 'font-semibold text-vintage-accent bg-vintage-paper' : 'text-vintage-ink'
                  }`}
                >
                  About
                </Link>
                <Link 
                  to="/blog" 
                  onClick={(e) => handleNavClick(e, '/blog')}
                  className={`vintage-link px-3 py-2 text-sm transition-all rounded-lg hover:bg-vintage-paper ${
                    isActive('/blog') ? 'font-semibold text-vintage-accent bg-vintage-paper' : 'text-vintage-ink'
                  }`}
                >
                  Essays
                </Link>
                <Link 
                  to="/initiatives" 
                  onClick={(e) => handleNavClick(e, '/initiatives')}
                  className={`vintage-link px-3 py-2 text-sm transition-all rounded-lg hover:bg-vintage-paper ${
                    isActive('/initiatives') || isActive('/projects') ? 'font-semibold text-vintage-accent bg-vintage-paper' : 'text-vintage-ink'
                  }`}
                >
                  Initiatives
                </Link>
                <Link 
                  to="/contact" 
                  onClick={(e) => handleNavClick(e, '/contact')}
                  className={`vintage-link px-3 py-2 text-sm transition-all rounded-lg hover:bg-vintage-paper ${
                    isActive('/contact') ? 'font-semibold text-vintage-accent bg-vintage-paper' : 'text-vintage-ink'
                  }`}
                >
                  Contact
                </Link>

                {/* Admin Button - Better Integration */}
                {isAdmin && (
                  <button
                    onClick={onAdminClick}
                    className="bg-vintage-accent text-vintage-cream px-3 py-2 rounded-lg hover:bg-opacity-80 transition-all text-xs font-medium flex items-center space-x-2"
                    title="Admin Panel"
                  >
                    <Edit3 size={14} />
                    <span>Admin</span>
                  </button>
                )}
              </div>
            </nav>
          ) : (
            /* Mobile Menu Button */
            <button 
              className="p-2 text-vintage-ink hover:text-vintage-accent transition-colors rounded-lg hover:bg-vintage-paper"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              {isMenuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          )}
        </div>

        {/* Mobile Menu */}
        {isMobile && isMenuOpen && (
          <div className="mt-4 pt-4 border-t border-vintage-muted bg-vintage-cream rounded-lg">
            <nav className="space-y-3 p-4">
              <Link 
                to="/about" 
                onClick={(e) => handleNavClick(e, '/about')}
                className={`block vintage-link text-sm px-3 py-2 rounded-lg transition-all ${
                  isActive('/about') ? 'font-semibold text-vintage-accent bg-vintage-paper' : 'text-vintage-ink hover:bg-vintage-paper'
                }`}
              >
                About
              </Link>
              <Link 
                to="/blog" 
                onClick={(e) => handleNavClick(e, '/blog')}
                className={`block vintage-link text-sm px-3 py-2 rounded-lg transition-all ${
                  isActive('/blog') ? 'font-semibold text-vintage-accent bg-vintage-paper' : 'text-vintage-ink hover:bg-vintage-paper'
                }`}
              >
                Essays
              </Link>
              <Link 
                to="/initiatives" 
                onClick={(e) => handleNavClick(e, '/initiatives')}
                className={`block vintage-link text-sm px-3 py-2 rounded-lg transition-all ${
                  isActive('/initiatives') || isActive('/projects') ? 'font-semibold text-vintage-accent bg-vintage-paper' : 'text-vintage-ink hover:bg-vintage-paper'
                }`}
              >
                Initiatives
              </Link>
              <Link 
                to="/contact" 
                onClick={(e) => handleNavClick(e, '/contact')}
                className={`block vintage-link text-sm px-3 py-2 rounded-lg transition-all ${
                  isActive('/contact') ? 'font-semibold text-vintage-accent bg-vintage-paper' : 'text-vintage-ink hover:bg-vintage-paper'
                }`}
              >
                Contact
              </Link>
              
              {/* Mobile Admin Access */}
              {isAdmin && (
                <button
                  onClick={() => {
                    onAdminClick();
                    closeMenus();
                  }}
                  className="w-full text-left bg-vintage-accent text-vintage-cream px-3 py-2 rounded-lg hover:bg-opacity-80 transition-all text-sm font-medium flex items-center space-x-2"
                >
                  <Edit3 size={14} />
                  <span>Admin Panel</span>
                </button>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default VintageHeader;